import { useCallback, useEffect, useState } from "react";
import {
  ArrowRight,
  Building2,
  CalendarDays,
  Landmark,
  LogIn,
  Megaphone,
  RefreshCw,
  Users2,
} from "lucide-react";
import { fetchNevisignCase, refreshAdliranCase } from "@nevisign/reader-core";
import type { CaseDetailDTO, CaseParty } from "@platform/shared-types";
import { INTERNAL_CASE_STATUS_LABELS_FA } from "@platform/shared-types";
import { useAuth } from "../contexts/auth-context";
import { ensureFreshAccessToken } from "../lib/platform-auth";
import { handleApiAuthError } from "../lib/auth-required";

type Props = {
  caseId: string;
  apiBase: string;
  onBack: () => void;
};

function faNum(n: number | string): string {
  return String(n).replace(/\d/g, (d) => "۰۱۲۳۴۵۶۷۸۹"[Number(d)]!);
}

function faDate(jalali: string | null | undefined): string {
  return jalali?.trim() || "—";
}

function partiesLine(parties: CaseParty[], max = 4): string {
  if (!parties.length) return "طرفی ثبت نشده است";
  const names = parties.slice(0, max).map((p) => {
    const roles = p.roleLabels?.length ? ` (${p.roleLabels.join("، ")})` : "";
    return `${p.name}${roles}`;
  });
  const extra = parties.length - names.length;
  return extra > 0
    ? `${names.join("، ")} و ${faNum(extra)} نفر دیگر`
    : names.join("، ");
}

export function CaseDetailPage({ caseId, apiBase, onBack }: Props) {
  const { isAuthenticated, openLogin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detail, setDetail] = useState<CaseDetailDTO | null>(null);

  const load = useCallback(async () => {
    if (!isAuthenticated) {
      setDetail(null);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    await ensureFreshAccessToken();
    const result = await fetchNevisignCase(apiBase, caseId);
    if (result.error && !result.case) {
      const err = new Error(result.error);
      if (handleApiAuthError(err, () => void load())) return;
      setError(result.error);
      setDetail(null);
      setLoading(false);
      return;
    }
    setDetail(result.case as unknown as CaseDetailDTO);
    setLoading(false);
  }, [apiBase, caseId, isAuthenticated]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!isAuthenticated) {
    return (
      <div className="case-page consumer-shell">
        <button type="button" className="case-page-back" onClick={onBack}>
          <ArrowRight size={16} />
          بازگشت به داشبورد
        </button>
        <div className="dashboard-auth-prompt">
          <p>برای مشاهده پرونده وارد حساب نویساین شوید.</p>
          <button type="button" className="icon-btn primary" onClick={() => openLogin()}>
            <LogIn size={16} />
            ورود
          </button>
        </div>
      </div>
    );
  }

  if (loading && !detail) {
    return (
      <div className="case-page consumer-shell">
        <button type="button" className="case-page-back" onClick={onBack}>
          <ArrowRight size={16} />
          بازگشت به داشبورد
        </button>
        <div className="dashboard-skeleton-list">
          <div className="dashboard-skeleton-row" />
          <div className="dashboard-skeleton-row" />
          <div className="dashboard-skeleton-row" />
        </div>
      </div>
    );
  }

  if (error || !detail) {
    return (
      <div className="case-page consumer-shell">
        <button type="button" className="case-page-back" onClick={onBack}>
          <ArrowRight size={16} />
          بازگشت به داشبورد
        </button>
        <div className="dashboard-empty">
          <p>{error || "پرونده یافت نشد"}</p>
          <button type="button" className="icon-btn" onClick={() => void load()}>
            <RefreshCw size={14} />
            تلاش مجدد
          </button>
        </div>
      </div>
    );
  }

  const c = detail;
  const statusLabel =
    INTERNAL_CASE_STATUS_LABELS_FA[c.internalStatus] ?? c.currentStatusLabel;
  const hearings = c.pendingActions ?? [];
  const notices = c.notices ?? [];
  const events = (c.events ?? []).slice(0, 8);

  return (
    <div className="case-page consumer-shell">
      <button type="button" className="case-page-back" onClick={onBack}>
        <ArrowRight size={16} />
        بازگشت
      </button>

      <header className="case-hero">
        <div className="case-hero-title-row">
          <h1>پرونده {faNum(c.caseNo)}</h1>
          <span className={`case-status-pill status-${c.internalStatus}`}>
            {statusLabel}
          </span>
          <button
            type="button"
            className="icon-btn compact"
            disabled={refreshing}
            onClick={() => {
              void (async () => {
                setRefreshing(true);
                await ensureFreshAccessToken();
                const sync = await refreshAdliranCase(apiBase, caseId);
                setRefreshing(false);
                if (!sync.ok) {
                  setError(sync.error ?? "به‌روزرسانی از عدل ایران ناموفق بود");
                  return;
                }
                await load();
              })();
            }}
          >
            <RefreshCw size={14} />
            {refreshing ? "در حال به‌روزرسانی…" : "به‌روزرسانی از عدل ایران"}
          </button>
        </div>
        <div className="case-meta-row">
          {c.primaryBranchName ? (
            <span>
              <Building2 size={14} /> {c.primaryBranchName}
            </span>
          ) : null}
          <span>
            <Landmark size={14} /> {c.primaryCourtName ?? "شعبه نامشخص"}
            {c.primaryCity ? ` · ${c.primaryCity}` : ""}
          </span>
          <span>
            <Users2 size={14} />{" "}
            {c.currentAssigneeName
              ? `مسئول: ${c.currentAssigneeName}`
              : "ارجاع‌نشده"}
          </span>
          {c.nextHearingDateJalali ? (
            <span>
              <CalendarDays size={14} /> جلسه بعد: {faDate(c.nextHearingDateJalali)}
            </span>
          ) : null}
        </div>
        {c.tags?.length ? (
          <div className="case-tags">
            {c.tags.map((t) => (
              <span key={t} className="case-tag">
                {t.replace(/_/g, " ")}
              </span>
            ))}
          </div>
        ) : null}
        <div className="hero-art" style={{ maxWidth: "10rem", marginTop: "0.75rem" }}>
          <img src="/illustrations/service-cases.webp" alt="" />
        </div>
      </header>

      <section className="case-card" aria-label="طرفین">
        <h2>
          <Users2 size={16} />
          طرفین
        </h2>
        {c.parties?.length ? (
          <ul className="case-party-list">
            {c.parties.map((p, i) => (
              <li key={`${p.name}-${i}`} className="case-party">
                <strong>{p.name}</strong>
                <span>
                  {p.roleLabels?.length ? p.roleLabels.join("، ") : "—"}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="dashboard-list-empty">{partiesLine([])}</p>
        )}
      </section>

      <section className="case-card" aria-label="موضوع">
        <h2>موضوع و اتهامات / خواسته</h2>
        {!c.subjectBlocks?.length ? (
          <p className="dashboard-list-empty">اطلاعاتی ثبت نشده است</p>
        ) : (
          <ul className="case-block-list">
            {c.subjectBlocks.map((s, i) => (
              <li key={i} className="case-block">
                <p className="case-block-title">{s.label}</p>
                {s.cause ? <p className="case-block-meta">{s.cause}</p> : null}
                {s.charges?.length ? (
                  <div className="case-tags">
                    {s.charges.map((ch, idx) => (
                      <span key={idx} className="case-tag">
                        {ch}
                      </span>
                    ))}
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="case-card" aria-label="جلسات">
        <h2>
          <CalendarDays size={16} />
          جلسات و اوقات رسیدگی
        </h2>
        {!hearings.length ? (
          <p className="dashboard-list-empty">جلسه یا وقت رسیدگی ثبت نشده است</p>
        ) : (
          <ul className="case-block-list">
            {hearings.map((h, i) => (
              <li key={i} className="case-block">
                <div className="case-block-row">
                  <span className="case-block-title">{h.label}</span>
                  <span className="case-block-meta tabular-fa">
                    {faDate(h.dateJalali)} {h.time ?? ""}
                  </span>
                </div>
                {h.judicialRow?.courtUnitName ? (
                  <p className="case-block-meta">{h.judicialRow.courtUnitName}</p>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="case-card" aria-label="ابلاغیه‌ها">
        <h2>
          <Megaphone size={16} />
          ابلاغیه‌ها
        </h2>
        {!notices.length ? (
          <p className="dashboard-list-empty">ابلاغیه‌ای ثبت نشده است</p>
        ) : (
          <ul className="case-block-list">
            {notices.map((n, i) => (
              <li key={i} className="case-block">
                <div className="case-block-row">
                  <span className="case-block-title">{n.label}</span>
                  <span className="case-block-meta">
                    {n.delivery?.label ?? "نامشخص"}
                  </span>
                </div>
                <p className="case-block-meta tabular-fa">
                  {faDate(n.noticeDateJalali)}
                </p>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="case-card" aria-label="رویدادها">
        <h2>رویدادهای اخیر</h2>
        {!events.length ? (
          <p className="dashboard-list-empty">رویدادی ثبت نشده است</p>
        ) : (
          <ul className="case-block-list">
            {events.map((e, i) => (
              <li key={i} className="case-block">
                <div className="case-block-row">
                  <span className="case-block-title">{e.label}</span>
                  <span className="case-block-meta tabular-fa">
                    {faDate(e.eventDateJalali)}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
